import { cachedFetch } from './api';
import { getFetchMeta } from './fetchWithCache';

export const BYBIT = 'https://api.bybit.com/v5';
export const BYBIT_TICKERS_URL = `${BYBIT}/market/tickers?category=linear`;

/* One request for every linear perpetual (~670 KB raw). Only the fields the
   terminal reads are kept, so the localStorage copy stays small. */
function slimTickers(json) {
  const list = (json?.result?.list ?? []).map(t => ({
    symbol: t.symbol,
    lastPrice: t.lastPrice,
    price24hPcnt: t.price24hPcnt,
    fundingRate: t.fundingRate,
    fundingIntervalHour: t.fundingIntervalHour,
    nextFundingTime: t.nextFundingTime,
    openInterestValue: t.openInterestValue,
    indexPrice: t.indexPrice,
    markPrice: t.markPrice,
  }));
  return { result: { list } };
}

/** All Bybit linear tickers as { [symbol]: ticker }. */
export async function fetchLinearTickers(ttlMs = 60 * 1000) {
  const json = await cachedFetch(BYBIT_TICKERS_URL, ttlMs, { transform: slimTickers });
  const map = {};
  for (const t of json?.result?.list ?? []) map[t.symbol] = t;
  return map;
}

export function tickersMeta() {
  return getFetchMeta(BYBIT_TICKERS_URL);
}

/* CoinGecko symbol → Bybit linear perpetual. Low-priced coins trade as
   1000PEPEUSDT, 1000BONKUSDT, SHIB1000USDT etc.; the price check (±15% of the
   CoinGecko price after the multiplier) also rules out unrelated contracts
   that happen to share a ticker. */
const CANDIDATES = [
  (s) => [`${s}USDT`, 1],
  (s) => [`1000${s}USDT`, 1000],
  (s) => [`10000${s}USDT`, 10000],
  (s) => [`1000000${s}USDT`, 1000000],
  (s) => [`${s}1000USDT`, 1000],
];

// CoinGecko ticker → Bybit contract name where the two differ.
const ALIASES = { PUMP: 'PUMPFUNUSDT', RAY: 'RAYDIUMUSDT' };

const STABLES = new Set(['USDT', 'USDC', 'DAI', 'FDUSD', 'USDE', 'USDS', 'PYUSD', 'TUSD', 'USDD', 'BUSD', 'USD1', 'RLUSD', 'USDT0', 'USDTB', 'BUIDL', 'USYC', 'USDF', 'USDG']);

export function resolveBybitSymbol(cgSymbol, cgPrice, tickerMap) {
  if (!cgSymbol || !tickerMap) return null;
  const s = cgSymbol.toUpperCase();
  if (STABLES.has(s)) return null;
  const alias = ALIASES[s];
  if (alias && tickerMap[alias]) {
    const p = parseFloat(tickerMap[alias].lastPrice);
    if (!cgPrice || !p || Math.abs(p - cgPrice) / cgPrice < 0.15) return alias;
  }
  for (const make of CANDIDATES) {
    const [sym, mult] = make(s);
    const t = tickerMap[sym];
    if (!t) continue;
    const p = parseFloat(t.lastPrice) / mult;
    if (!cgPrice || !p || Math.abs(p - cgPrice) / cgPrice < 0.15) return sym;
  }
  return null;
}
